// Option lists for the Lineage Atlas submission form. Kept centralised so
// labels stay consistent across the submit modal, admin review surfaces, and
// the public detail page when (later) we render the same vocabulary back.
//
// Convention: tuples are [value, label]. `value` is canonical and stored on
// the record; `label` is shown to humans. Add new options at the END to keep
// admin UX stable.

export const GENETIC_ENTRY_TYPES = [
  ["modern_hybrid",          "Modern hybrid"],
  ["landrace",               "Landrace"],
  ["heirloom",               "Heirloom"],
  ["ibl",                    "IBL / inbred line"],
  ["f_generation",           "F-generation / filial line"],
  ["backcross",              "Backcross / BX"],
  ["s1_selfed",              "S1 / selfed line"],
  ["clone_only",             "Clone-only cultivar"],
  ["open_pollinated",        "Open-pollinated population"],
  ["preservation_accession", "Preservation accession"],
  ["unknown_disputed",       "Unknown / disputed"],
];

// Entry types that consume the Parent 1 / Parent 2 lineage block.
export const PARENT_DRIVEN_ENTRY_TYPES = new Set([
  "modern_hybrid", "f_generation", "backcross", "s1_selfed",
]);

// Entry types that consume the Landrace / Origin block.
export const ORIGIN_DRIVEN_ENTRY_TYPES = new Set([
  "landrace", "heirloom", "open_pollinated", "preservation_accession",
]);

// Entry types that consume the Clone-only block.
export const CLONE_DRIVEN_ENTRY_TYPES = new Set([
  "clone_only",
]);

export const PARENT_ROLES = [
  ["female",          "Female"],
  ["male",            "Male"],
  ["reversed_female", "Reversed female"],
  ["pollen_donor",    "Pollen donor"],
  ["unknown",         "Unknown"],
];

export const PARENT_TYPES = [
  ["clone",        "Clone"],
  ["seed_line",    "Seed line"],
  ["landrace",     "Landrace"],
  ["ibl",          "IBL"],
  ["f_generation", "F-generation"],
  ["backcross",    "BX"],
  ["s1_selfed",    "S1"],
  ["unknown",      "Unknown"],
];

export const GENERATIONS = [
  ["f1", "F1"], ["f2", "F2"], ["f3", "F3"], ["f4", "F4"], ["f5", "F5"], ["f6_plus", "F6+"],
  ["bx1", "BX1"], ["bx2", "BX2"], ["bx3_plus", "BX3+"],
  ["s1", "S1"], ["s2", "S2"], ["r1", "R1"],
  ["ibl", "IBL"], ["unknown", "Unknown"],
];

export const SELECTION_DIRECTIONS = [
  ["resin", "Resin"], ["terpene_profile", "Terpene profile"],
  ["yield", "Yield"], ["mold_resistance", "Mold resistance"],
  ["short_flowering", "Short flowering"], ["color", "Color"],
  ["hash_production", "Hash production"], ["cannabinoid_ratio", "Cannabinoid ratio"],
  ["preservation", "Preservation"], ["other", "Other"],
];

export const POPULATION_TYPES = [
  ["wild", "Wild"], ["feral", "Feral"], ["farmer_maintained", "Farmer-maintained"],
  ["hash_production_field", "Hash-production field"],
  ["mountain_village", "Mountain village cultivar"],
  ["tropical_ganja", "Tropical ganja line"],
  ["fiber_hemp_admixed", "Fiber / hemp-admixed"],
  ["unknown", "Unknown"],
];

export const PRESERVATION_STATUSES = [
  ["original_population", "Original population"],
  ["reproduced_in_country", "Reproduced in-country"],
  ["reproduced_ex_situ", "Reproduced ex situ"],
  ["bottlenecked", "Bottlenecked"],
  ["hybrid_contaminated", "Hybrid-contaminated"],
  ["disputed", "Disputed"], ["lost", "Lost"], ["unknown", "Unknown"],
];

export const ENVIRONMENT_ADAPTATIONS = [
  ["mountain", "Mountain"], ["tropical", "Tropical"], ["desert", "Desert"],
  ["humid", "Humid"], ["cold_tolerant", "Cold-tolerant"],
  ["drought_tolerant", "Drought-tolerant"], ["mold_resistant", "Mold-resistant"],
  ["indoor_selected", "Indoor-selected"], ["greenhouse_selected", "Greenhouse-selected"],
  ["unknown", "Unknown"],
];

export const CUT_AUTHENTICITY = [
  ["verified", "Verified"], ["breeder_held", "Breeder-held"],
  ["community_accepted", "Community-accepted"], ["disputed", "Disputed"],
  ["unknown", "Unknown"],
];

export const YES_NO_UNKNOWN = [
  ["yes", "Yes"], ["no", "No"], ["unknown", "Unknown"],
];

export const LINEAGE_CONFIDENCE = [
  ["verified_breeder_record", "Verified by breeder record"],
  ["verified_genetic_test",   "Verified by genetic test"],
  ["verified_seed_pack",      "Verified by seed pack / catalog"],
  ["reported_by_breeder",     "Reported by breeder"],
  ["community_accepted",      "Community accepted"],
  ["unverified",              "Unverified"],
  ["disputed",                "Disputed"],
  ["unknown",                 "Unknown"],
];

export const EVIDENCE_TYPES = [
  ["breeder_notes", "Breeder notes"], ["seed_pack", "Seed pack"],
  ["website_listing", "Website listing"], ["genetic_marker_test", "Genetic marker test"],
  ["lab_report", "Lab report"], ["coa", "COA"],
  ["interview", "Interview"], ["forum_archive", "Forum archive"],
  ["catalog_scan", "Catalog scan"], ["personal_breeding_record", "Personal breeding record"],
  ["community_submission", "Community submission"],
];

export const DISPUTE_STATUSES = [
  ["no_dispute", "No dispute"],
  ["disputed_parentage", "Disputed parentage"],
  ["disputed_breeder", "Disputed breeder"],
  ["disputed_name", "Disputed name"],
  ["duplicate_entry", "Duplicate entry"],
  ["clone_authenticity_disputed", "Clone authenticity disputed"],
  ["landrace_origin_disputed", "Landrace origin disputed"],
];

export const GENETIC_MARKER_TYPES = [
  ["snp", "SNP"], ["str", "STR"],
  ["whole_genome", "Whole genome sequencing"],
  ["reduced_representation", "Reduced representation sequencing"],
  ["chemotype_panel", "Chemotype marker panel"],
  ["unknown", "Unknown"],
];

export const CHEMOTYPES = [
  ["type_i",  "Type I — THC dominant"],
  ["type_ii", "Type II — THC/CBD mixed"],
  ["type_iii","Type III — CBD dominant"],
  ["type_iv", "Type IV — CBG dominant"],
  ["type_v",  "Type V — low/no cannabinoid"],
  ["unknown", "Unknown"],
];

export const LEAF_TYPES = [
  ["broadleaf", "Broadleaf"], ["narrowleaf", "Narrowleaf"],
  ["mixed", "Mixed"], ["unknown", "Unknown"],
];

export const PHOTOPERIOD_TYPES = [
  ["photoperiod", "Photoperiod"], ["autoflower", "Autoflower"],
  ["semi_auto", "Semi-auto"], ["unknown", "Unknown"],
];

export const SEX_EXPRESSIONS = [
  ["dioecious", "Dioecious"], ["monoecious_tendency", "Monoecious tendency"],
  ["feminized", "Feminized"], ["herm_prone", "Herm-prone"],
  ["stable", "Stable"], ["unknown", "Unknown"],
];

export const RESIN_PRODUCTION = [
  ["low", "Low"], ["moderate", "Moderate"],
  ["high", "High"], ["extreme", "Extreme"], ["unknown", "Unknown"],
];

export const EVIDENCE_FILE_CATEGORIES = [
  ["seed_pack",     "Seed pack photo"],
  ["plant_photo",   "Plant photo"],
  ["flower_photo",  "Flower photo"],
  ["leaf_photo",    "Leaf photo"],
  ["lab_report",    "Lab report"],
  ["breeder_note",  "Breeder note screenshot"],
  ["catalog_scan",  "Catalog scan"],
  ["coa",           "COA"],
  ["genetic_test",  "Genetic test report"],
  ["other",         "Other"],
];

export const lookupLabel = (options, value) => {
  const hit = (options || []).find((o) => o[0] === value);
  return hit ? hit[1] : (value || "");
};
